import React from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { useRootStore } from "../../../../store/contexts";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { FormSaveFooter } from "../../../../common/controls/forms/FormSaveFooter";

import FormExtras from "../../../../common/components/formExtras";
import {
  showcaseWorkflow,
  SHOWCASE_WORKFLOW_ERROR_STATES,
  SHOWCASE_WORKFLOW_FORWARD_TRANSITIONS,
} from "../../../../workflows/workflowDefinitions";
import { DownloadIcon } from "../../../../icons";
import { VisibilityIcon } from "../../../../icons";
import { PDFViewerDialog } from "../../../../common/components/PDFViewerDialog";
import { isOriginalCrudStatus, RowCrudStatus } from "../../../common/types";
import { DesignShowcaseUpdateStore } from "../../store";
import { runInAction } from "mobx";
import { ItemsCRUDCompact } from "../../../common/controls";
import { CrudRow } from "../../../common/types";
import { GridChangeSet } from "../../../common/types";
import { ShowcaseLineUpdate } from "../../../showcaseLine/components/update/ShowcaseLineUpdate";
import { ShowcaseLineUpdateStore } from "../../../showcaseLine/store";
import { GridColumn } from "../../../common/types";
import { FormControl } from "@mui/material";
import { InputString } from "../../../common/controls";
import { InputText } from "../../../common/controls";
import { ShowcaseStatus } from "../../../../api/graphql";
import { InputReference } from "../../../common/controls";
import { InputReferenceSet } from "../../../common/controls";
import { CreateEntityDialog } from "../../../common/controls";
import { ShowcaseCategoryCreate } from "../../../showcaseCategory/components/create/ShowcaseCategoryCreate";
import { ShowcaseCategoryCreateStore } from "../../../showcaseCategory/store";
import { InputReferenceCheckboxes } from "../../../common/controls";
import { WorkflowRoadmap } from "../../../../common/controls";
import { InputNumber } from "../../../common/controls";
import { InputIntegerChoices } from "../../../common/controls";
import { InputDate } from "../../../common/controls";
import { InputDateTime } from "../../../common/controls";
import { InputBoolean } from "../../../common/controls";
import { InputPhone } from "../../../common/controls";
import { InputStringMask } from "../../../common/controls";
import { FileCRUDType } from "../../../common/types";
import { LoadFilesCRUDCompact } from "../../../common/controls";
import { AvatarField } from "../../../common/controls";
import { InputStringList } from "../../../common/controls";
import { InputHtmlString } from "../../../common/controls";
import { InputCodeEditor } from "../../../common/controls";
import { InputSecret } from "../../../common/controls";

export type DesignShowcaseUpdateFormProps = {
  onSave?: () => void;
  onCancel?: () => void;
  /** Mientras se guarda, Guardar queda deshabilitado. */
  saving?: boolean;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
  linesOnRestore: (id: string) => void;
  linesOnCommitChanges: (changes: GridChangeSet) => void;
  linesOnCommitInlineChange: (
    rowId: string,
    changes: Record<string, any>
  ) => void;
  linesValidateChanges: (changes: GridChangeSet) => boolean;
  linesRows: CrudRow[];
  linesError?: string;
  showcaseLineUpdateStore: ShowcaseLineUpdateStore;
  linesVisibleColumns: GridColumn[];
  linesColumnTypes: Record<
    string,
    {
      fieldType:
        | "String"
        | "Int"
        | "Decimal"
        | "Boolean"
        | "Date"
        | "DateTime"
        | "__Reference"
        | "File"
        | "__Enum";
      nullable?: boolean;
      readOnly?: boolean;
    }
  >;
  code: string;
  codeSelectedValue?: { displayId: string; id: string } | null;
  onCodeChange?: (value: string) => void;
  codeError?: string;
  name: string;
  nameSelectedValue?: { displayId: string; id: string } | null;
  onNameChange?: (value: string) => void;
  nameError?: string;
  summary: string;
  summarySelectedValue?: { displayId: string; id: string } | null;
  onSummaryChange?: (value: string) => void;
  summaryError?: string;
  statusOptions: { id: string; displayId: string }[];
  statusError?: string;
  status: ShowcaseStatus;
  onStatusChange?: (value: ShowcaseStatus) => void;
  categorySelectedValue?: { displayId: string; id: string } | null;
  onCategoryChange?: (value: string) => void;
  onCategorySelected?: (
    value: { displayId: string; id: string } | null
  ) => void;
  categoryOptions?: { displayId: string; id: string }[];
  categoryError?: string;
  viewFormCategory?: () => void;
  relatedCategoriesSelectedValues: { displayId: string; id: string }[];
  onRelatedCategoriesChange?: (value: string) => void;
  onRelatedCategoriesSelected?: (
    value: { displayId: string; id: string }[]
  ) => void;
  relatedCategoriesOptions?: { displayId: string; id: string }[];
  relatedCategoriesError?: string;
  appliedStatusesOptions: { id: string; displayId: string }[];
  appliedStatusesError?: string;
  appliedStatuses: ShowcaseStatus[];
  onAppliedStatusesChange?: (value: ShowcaseStatus[]) => void;
  lifecycleTransitions: { id: string; label: string }[];
  onLifecycleTransition: (transitionName: string) => {};
  lifecycleValue: string;
  lifecycleIsProcessing?: boolean;
  quantity: number;
  quantitySelectedValue?: { displayId: string; id: string } | null;
  onQuantityChange?: (value: number) => void;
  quantityError?: string;
  durationInYears: number | undefined;
  durationInYearsSelectedValue?: { displayId: string; id: string } | null;
  onDurationInYearsChange?: (value: number | undefined) => void;
  durationInYearsError?: string;
  amount: number;
  amountSelectedValue?: { displayId: string; id: string } | null;
  onAmountChange?: (value: number) => void;
  amountError?: string;
  ratio: number | undefined;
  ratioSelectedValue?: { displayId: string; id: string } | null;
  onRatioChange?: (value: number | undefined) => void;
  ratioError?: string;
  startDate: string | null;
  startDateSelectedValue?: { displayId: string; id: string } | null;
  onStartDateChange?: (value: string | null) => void;
  startDateError?: string;
  expiresAt: Date | null;
  expiresAtSelectedValue?: { displayId: string; id: string } | null;
  onExpiresAtChange?: (value: Date | null) => void;
  expiresAtError?: string;
  registeredAt: Date | null;
  registeredAtSelectedValue?: { displayId: string; id: string } | null;
  onRegisteredAtChange?: (value: Date | null) => void;
  registeredAtError?: string;
  active: boolean;
  activeSelectedValue?: { displayId: string; id: string } | null;
  onActiveChange?: (value: boolean) => void;
  activeError?: string;
  highlighted: boolean;
  highlightedSelectedValue?: { displayId: string; id: string } | null;
  onHighlightedChange?: (value: boolean) => void;
  highlightedError?: string;
  phone: string;
  phoneSelectedValue?: { displayId: string; id: string } | null;
  onPhoneChange?: (value: string) => void;
  phoneError?: string;
  taxId: string;
  taxIdSelectedValue?: { displayId: string; id: string } | null;
  onTaxIdChange?: (value: string) => void;
  taxIdError?: string;
  attachmentOnRestoreFile: (id: string) => void;
  attachmentOnLoadFile: (file: any) => void;
  attachmentOnCommitFile: (changes: GridChangeSet) => void;
  attachmentFiles: FileCRUDType[];
  attachmentError?: string;
  anyFileOnRestoreFile: (id: string) => void;
  anyFileOnLoadFile: (file: any) => void;
  anyFileOnCommitFile: (changes: GridChangeSet) => void;
  anyFileFiles: FileCRUDType[];
  anyFileError?: string;
  avatarData?: any;
  avatarEntityId: string;
  avatarPreviewUrl?: string | null;
  onAvatarChange: (blob: Blob) => void;
  onAvatarDelete: () => void;
  keywords: string[];
  keywordsSelectedValue?: { displayId: string; id: string } | null;
  onKeywordsChange?: (value: string[]) => void;
  keywordsError?: string;
  body: string;
  bodySelectedValue?: { displayId: string; id: string } | null;
  onBodyChange?: (value: string) => void;
  bodyError?: string;
  metadata: string;
  metadataSelectedValue?: { displayId: string; id: string } | null;
  onMetadataChange?: (value: string) => void;
  metadataError?: string;
  internalNote: string;
  internalNoteSelectedValue?: { displayId: string; id: string } | null;
  onInternalNoteChange?: (value: string) => void;
  internalNoteError?: string;
  apiKey: string;
  apiKeySelectedValue?: { displayId: string; id: string } | null;
  onApiKeyChange?: (value: string) => void;
  apiKeyError?: string;
  dummyCRUDRestoredObservable: number;
  designShowcaseUpdateStore: DesignShowcaseUpdateStore;
};

export const DesignShowcaseUpdateForm: React.FC<DesignShowcaseUpdateFormProps> =
  observer(
    ({
      onSave = () => {},
      onCancel = () => {},
      saving = false,
      showToolbox = true,
      showSaveFooter,
      linesOnRestore,
      linesOnCommitChanges,
      linesOnCommitInlineChange,
      linesValidateChanges,
      linesRows,
      linesError,
      showcaseLineUpdateStore,
      linesVisibleColumns,
      linesColumnTypes,
      code = "",
      codeError,
      onCodeChange = () => {},
      name = "",
      nameError,
      onNameChange = () => {},
      summary = "",
      summaryError,
      onSummaryChange = () => {},
      statusOptions = [],
      statusError,
      status = "" as ShowcaseStatus,
      onStatusChange = () => {},
      onCategorySelected = () => {},
      categoryOptions = [],
      categorySelectedValue = null,
      categoryError,
      onCategoryChange = () => {},
      viewFormCategory = () => {},
      onRelatedCategoriesSelected = () => {},
      relatedCategoriesOptions = [],
      relatedCategoriesSelectedValues,
      relatedCategoriesError,
      onRelatedCategoriesChange = () => {},
      appliedStatusesOptions = [],
      appliedStatusesError,
      appliedStatuses = [] as ShowcaseStatus[],
      onAppliedStatusesChange = () => {},
      lifecycleTransitions,
      onLifecycleTransition,
      lifecycleValue,
      lifecycleIsProcessing,
      quantity = 0,
      quantityError,
      onQuantityChange = () => {},
      durationInYears = undefined,
      durationInYearsError,
      onDurationInYearsChange = () => {},
      amount = 0,
      amountError,
      onAmountChange = () => {},
      ratio = undefined,
      ratioError,
      onRatioChange = () => {},
      startDate = null,
      startDateError,
      onStartDateChange = () => {},
      expiresAt = null,
      expiresAtError,
      onExpiresAtChange = () => {},
      registeredAt = null,
      registeredAtError,
      onRegisteredAtChange = () => {},
      active = false,
      activeError,
      onActiveChange = () => {},
      highlighted = false,
      highlightedError,
      onHighlightedChange = () => {},
      phone = "",
      phoneError,
      onPhoneChange = () => {},
      taxId = "",
      taxIdError,
      onTaxIdChange = () => {},
      attachmentOnRestoreFile,
      attachmentOnLoadFile,
      attachmentOnCommitFile,
      attachmentFiles,
      attachmentError,
      anyFileOnRestoreFile,
      anyFileOnLoadFile,
      anyFileOnCommitFile,
      anyFileFiles,
      anyFileError,
      avatarData,
      avatarEntityId,
      avatarPreviewUrl,
      onAvatarChange,
      onAvatarDelete,
      keywords = [],
      keywordsError,
      onKeywordsChange = () => {},
      body = "",
      bodyError,
      onBodyChange = () => {},
      metadata = "{}",
      metadataError,
      onMetadataChange = () => {},
      internalNote = "",
      internalNoteError,
      onInternalNoteChange = () => {},
      apiKey = "",
      apiKeyError,
      onApiKeyChange = () => {},
      dummyCRUDRestoredObservable,
      designShowcaseUpdateStore,
    }) => {
      const rootStore = useRootStore();
      const { langStore } = rootStore;

      if (showcaseLineUpdateStore.status === "uninitialized") {
        showcaseLineUpdateStore.initialize();
      }

      const internalNoteIsVisible = ["SysAdmin"].some((role) =>
        rootStore.authStore.hasRole(role)
      );

      const getViewAction = (
        onDownload: (props: {
          id: string;
          type: string;
          name: string;
          __status__: RowCrudStatus;
        }) => void,
        onViewPdf?: (props: any) => void
      ) => {
        const actions: any[] = [];
        if (onViewPdf) {
          actions.push({
            columnName: "__action_view_pdf",
            onClick: (props: any) =>
              isOriginalCrudStatus(props.__status__) ? onViewPdf(props) : {},
            icon: <VisibilityIcon />,
            label: langStore.viewCommand,
            isActionEnabled: (props: any) =>
              props.name?.toLowerCase().endsWith(".pdf"),
          });
        }
        actions.push({
          columnName: "__action_view",
          onClick: (props: any) =>
            isOriginalCrudStatus(props.__status__) ? onDownload(props) : {},
          icon: <DownloadIcon />,
          label: langStore.downloadCommand,
        });
        return actions;
      };
      return (
        <Grid>
          {showToolbox && (
            <FormToolbox
              saveAndCloseLabel={langStore.saveEntity}
              closeLabel={langStore.cancelUpdatedEntity}
              saveForm={() => onSave()}
              saveDisabled={saving}
              closeForm={() => onCancel()}
            />
          )}
          <Box>
            <FormExtras
              entity="DesignShowcase"
              mode="update"
              values={{}}
              options={{ category: categoryOptions }}
              onPatch={(campo: string, valor: any) => {
                switch (campo) {
                  case "code":
                    onCodeChange?.(valor);
                    break;
                  case "name":
                    onNameChange?.(valor);
                    break;
                  case "summary":
                    onSummaryChange?.(valor);
                    break;
                  case "status":
                    onStatusChange?.(valor);
                    break;
                  case "category":
                    onCategorySelected?.(valor);
                    break;
                  case "quantity":
                    onQuantityChange?.(valor);
                    break;
                  case "durationInYears":
                    onDurationInYearsChange?.(valor);
                    break;
                  case "amount":
                    onAmountChange?.(valor);
                    break;
                  case "ratio":
                    onRatioChange?.(valor);
                    break;
                  case "startDate":
                    onStartDateChange?.(valor);
                    break;
                  case "expiresAt":
                    onExpiresAtChange?.(valor);
                    break;
                  case "registeredAt":
                    onRegisteredAtChange?.(valor);
                    break;
                  case "active":
                    onActiveChange?.(valor);
                    break;
                  case "highlighted":
                    onHighlightedChange?.(valor);
                    break;
                  case "phone":
                    onPhoneChange?.(valor);
                    break;
                  case "taxId":
                    onTaxIdChange?.(valor);
                    break;
                  case "keywords":
                    onKeywordsChange?.(valor);
                    break;
                  case "body":
                    onBodyChange?.(valor);
                    break;
                  case "metadata":
                    onMetadataChange?.(valor);
                    break;
                  case "internalNote":
                    onInternalNoteChange?.(valor);
                    break;
                  case "apiKey":
                    onApiKeyChange?.(valor);
                    break;
                }
              }}
            />
            <GroupContainer
              id="null"
              errorText={designShowcaseUpdateStore.getCardErrors("null")}
              title={langStore.groupDesignShowcaseLabel}
              collapsible={true}
            >
              <FormControl fullWidth>
                <ItemsCRUDCompact
                  label={langStore.DesignShowcaseLinesLabel}
                  description={langStore.DesignShowcaseLinesDescription}
                  showLabel={false}
                  rows={linesRows}
                  columns={linesVisibleColumns}
                  columnTypes={linesColumnTypes}
                  canCreate={true}
                  isEditVisible={() =>
                    rootStore.authStore.hasPermissions("showcaseLine_update")
                  }
                  isDeleteVisible={() =>
                    rootStore.authStore.hasPermissions("showcaseLine_delete")
                  }
                  popupComponent={(row: any) => {
                    if (
                      row &&
                      row.id &&
                      !(
                        typeof row.id === "string" && row.id.startsWith("auto-")
                      )
                    ) {
                      runInAction(() =>
                        designShowcaseUpdateStore.linesLoadRow(row)
                      );
                    }
                    return (
                      <ShowcaseLineUpdate
                        showcaseLineUpdateStore={showcaseLineUpdateStore}
                        showToolbox={false}
                        showSaveFooter={false}
                      />
                    );
                  }}
                  popupMaxWidth="sm"
                  onRestore={(id: string) => linesOnRestore(id)}
                  onCommitChanges={(changes: GridChangeSet) =>
                    linesOnCommitChanges(changes)
                  }
                  onCommitInlineChange={(
                    rowId: string,
                    changes: Record<string, any>
                  ) => linesOnCommitInlineChange(rowId, changes)}
                  validate={(changes: GridChangeSet) =>
                    linesValidateChanges(changes)
                  }
                  dummyCRUDRestoredObservable={dummyCRUDRestoredObservable}
                  error={linesError}
                />
              </FormControl>
            </GroupContainer>

            <GroupContainer
              id="identification"
              errorText={designShowcaseUpdateStore.getCardErrors(
                "identification"
              )}
              title={langStore.groupDesignShowcaseIdentificationLabel}
              collapsible={true}
              expanded={designShowcaseUpdateStore.getCardState(
                "identification"
              )}
              toggleExpand={() =>
                designShowcaseUpdateStore.toggleCardState("identification")
              }
            >
              <FormControl fullWidth>
                <InputString
                  autoFocus
                  value={code}
                  error={Boolean(codeError)}
                  warning={
                    !codeError &&
                    Boolean(designShowcaseUpdateStore.warnings["code"])
                  }
                  helperText={
                    codeError ||
                    designShowcaseUpdateStore.warnings["code"]?.message
                  }
                  label={langStore.DesignShowcaseCodeLabel}
                  onChange={(value: string) => onCodeChange(value)}
                  description={langStore.DesignShowcaseCodeDescription}
                />
                <InputString
                  value={name}
                  error={Boolean(nameError)}
                  warning={
                    !nameError &&
                    Boolean(designShowcaseUpdateStore.warnings["name"])
                  }
                  helperText={
                    nameError ||
                    designShowcaseUpdateStore.warnings["name"]?.message
                  }
                  label={langStore.DesignShowcaseNameLabel}
                  onChange={(value: string) => onNameChange(value)}
                />
                <InputText
                  value={summary}
                  error={Boolean(summaryError)}
                  warning={
                    !summaryError &&
                    Boolean(designShowcaseUpdateStore.warnings["summary"])
                  }
                  helperText={
                    summaryError ||
                    designShowcaseUpdateStore.warnings["summary"]?.message
                  }
                  label={langStore.DesignShowcaseSummaryLabel}
                  onChange={(value: string) => onSummaryChange(value)}
                  nullText={langStore.optionalFieldHint}
                  description={langStore.DesignShowcaseSummaryDescription}
                />
              </FormControl>
            </GroupContainer>

            <GroupContainer
              id="classification"
              errorText={designShowcaseUpdateStore.getCardErrors(
                "classification"
              )}
              title={langStore.groupDesignShowcaseClassificationLabel}
              collapsible={true}
              expanded={designShowcaseUpdateStore.getCardState(
                "classification"
              )}
              toggleExpand={() =>
                designShowcaseUpdateStore.toggleCardState("classification")
              }
            >
              <FormControl fullWidth>
                <InputReference
                  id="status"
                  disabled={false}
                  error={Boolean(statusError)}
                  helperText={
                    statusError ||
                    designShowcaseUpdateStore.warnings["status"]?.message
                  }
                  label={langStore.DesignShowcaseStatusLabel}
                  options={statusOptions}
                  selectedValue={
                    status
                      ? {
                          id: status,
                          displayId:
                            statusOptions.find(
                              (o: { id: string }) => o.id === status
                            )?.displayId || status,
                        }
                      : null
                  }
                  onChange={() => {}}
                  onSelect={(value: { id: string; displayId: string } | null) =>
                    onStatusChange(
                      (value?.id as ShowcaseStatus) || ("" as ShowcaseStatus)
                    )
                  }
                />
                <InputReference
                  id="category"
                  label={langStore.DesignShowcaseCategoryLabel}
                  error={Boolean(categoryError)}
                  helperText={
                    categoryError ||
                    designShowcaseUpdateStore.warnings["category"]?.message
                  }
                  selectedValue={categorySelectedValue}
                  onChange={(value: string) => onCategoryChange(value)}
                  onSelect={(value: { displayId: string; id: string } | null) =>
                    onCategorySelected(value)
                  }
                  options={categoryOptions}
                  description={langStore.DesignShowcaseCategoryDescription}
                  onView={viewFormCategory}
                />
                <InputReferenceSet
                  id="relatedCategories"
                  label={langStore.DesignShowcaseRelatedCategoriesLabel}
                  error={Boolean(relatedCategoriesError)}
                  helperText={
                    relatedCategoriesError ||
                    designShowcaseUpdateStore.warnings["relatedCategories"]
                      ?.message
                  }
                  selectedValues={relatedCategoriesSelectedValues}
                  onChange={(value: string) => onRelatedCategoriesChange(value)}
                  onSelect={(value: { displayId: string; id: string }[]) =>
                    onRelatedCategoriesSelected(value)
                  }
                  options={relatedCategoriesOptions}
                  nullText={langStore.optionalFieldHint}
                  canCreate={rootStore.authStore.hasPermissions(
                    "showcaseCategory_create"
                  )}
                  renderCreateDialog={(dialogProps) => (
                    <CreateEntityDialog
                      {...dialogProps}
                      createStore={(rootStore) =>
                        new ShowcaseCategoryCreateStore(rootStore)
                      }
                      renderForm={(store) => (
                        <ShowcaseCategoryCreate
                          showcaseCategoryCreateStore={store}
                          showToolbox={false}
                          showSaveFooter={false}
                        />
                      )}
                      saveMethod="saveNewShowcaseCategory"
                      cancelMethod="cancelNewShowcaseCategory"
                    />
                  )}
                />
                <InputReferenceCheckboxes
                  id="appliedStatuses"
                  disabled={false}
                  error={Boolean(appliedStatusesError)}
                  helperText={
                    appliedStatusesError ||
                    designShowcaseUpdateStore.warnings["appliedStatuses"]
                      ?.message
                  }
                  label={langStore.DesignShowcaseAppliedStatusesLabel}
                  options={appliedStatusesOptions}
                  selectedValues={appliedStatuses.map((v: ShowcaseStatus) => ({
                    id: v,
                    displayId:
                      appliedStatusesOptions.find(
                        (o: { id: string }) => o.id === v
                      )?.displayId || v,
                  }))}
                  onSelect={(values: { id: string; displayId: string }[]) =>
                    onAppliedStatusesChange(
                      values.map((v) => v.id as ShowcaseStatus)
                    )
                  }
                />
                <WorkflowRoadmap
                  workflow={showcaseWorkflow}
                  currentState={lifecycleValue}
                  labels={langStore.DesignShowcaseLifecycleStateLabels}
                  transitions={lifecycleTransitions}
                  onTransition={(transitionId: string) =>
                    onLifecycleTransition(transitionId)
                  }
                  isProcessing={lifecycleIsProcessing}
                  errorStates={SHOWCASE_WORKFLOW_ERROR_STATES}
                  forwardTransitions={SHOWCASE_WORKFLOW_FORWARD_TRANSITIONS}
                />
              </FormControl>
            </GroupContainer>

            <GroupContainer
              id="numbers"
              errorText={designShowcaseUpdateStore.getCardErrors("numbers")}
              title={langStore.groupDesignShowcaseNumbersLabel}
              collapsible={true}
              expanded={designShowcaseUpdateStore.getCardState("numbers")}
              toggleExpand={() =>
                designShowcaseUpdateStore.toggleCardState("numbers")
              }
            >
              <FormControl fullWidth>
                <InputNumber
                  value={quantity}
                  error={Boolean(quantityError)}
                  warning={
                    !quantityError &&
                    Boolean(designShowcaseUpdateStore.warnings["quantity"])
                  }
                  helperText={
                    quantityError ||
                    designShowcaseUpdateStore.warnings["quantity"]?.message
                  }
                  label={langStore.DesignShowcaseQuantityLabel}
                  decimalScale={0}
                  onChange={(value: number) => onQuantityChange(value)}
                />
                <InputIntegerChoices
                  value={durationInYears}
                  nullable={true}
                  min={1}
                  max={50}
                  unitSingular={"año"}
                  unitPlural={"años"}
                  error={Boolean(durationInYearsError)}
                  helperText={
                    durationInYearsError ||
                    designShowcaseUpdateStore.warnings["durationInYears"]
                      ?.message
                  }
                  label={langStore.DesignShowcaseDurationInYearsLabel}
                  nullText={langStore.DesignShowcaseDurationInYearsNullText}
                  description={
                    langStore.DesignShowcaseDurationInYearsDescription
                  }
                  onChange={(value: number | undefined) =>
                    onDurationInYearsChange(value)
                  }
                />
                <InputNumber
                  value={amount}
                  error={Boolean(amountError)}
                  warning={
                    !amountError &&
                    Boolean(designShowcaseUpdateStore.warnings["amount"])
                  }
                  helperText={
                    amountError ||
                    designShowcaseUpdateStore.warnings["amount"]?.message
                  }
                  label={langStore.DesignShowcaseAmountLabel}
                  decimalScale={2}
                  prefix={"$"}
                  thousandSeparator={","}
                  fixedDecimalScale={true}
                  description={langStore.DesignShowcaseAmountDescription}
                  onChange={(value: number) => onAmountChange(value)}
                />
                <InputNumber
                  value={ratio}
                  nullable={true}
                  error={Boolean(ratioError)}
                  warning={
                    !ratioError &&
                    Boolean(designShowcaseUpdateStore.warnings["ratio"])
                  }
                  helperText={
                    ratioError ||
                    designShowcaseUpdateStore.warnings["ratio"]?.message
                  }
                  label={langStore.DesignShowcaseRatioLabel}
                  decimalScale={2}
                  nullText={langStore.optionalFieldHint}
                  onChange={(value: number | undefined) => onRatioChange(value)}
                />
              </FormControl>
            </GroupContainer>

            <GroupContainer
              id="dates"
              errorText={designShowcaseUpdateStore.getCardErrors("dates")}
              title={langStore.groupDesignShowcaseDatesLabel}
              collapsible={true}
              expanded={designShowcaseUpdateStore.getCardState("dates")}
              toggleExpand={() =>
                designShowcaseUpdateStore.toggleCardState("dates")
              }
            >
              <FormControl fullWidth>
                <InputDate
                  value={startDate}
                  error={Boolean(startDateError)}
                  warning={
                    !startDateError &&
                    Boolean(designShowcaseUpdateStore.warnings["startDate"])
                  }
                  helperText={
                    startDateError ||
                    designShowcaseUpdateStore.warnings["startDate"]?.message
                  }
                  label={langStore.DesignShowcaseStartDateLabel}
                  onChange={(value: string | null | null) =>
                    onStartDateChange(value)
                  }
                />
                <InputDateTime
                  value={expiresAt}
                  error={Boolean(expiresAtError)}
                  warning={
                    !expiresAtError &&
                    Boolean(designShowcaseUpdateStore.warnings["expiresAt"])
                  }
                  helperText={
                    expiresAtError ||
                    designShowcaseUpdateStore.warnings["expiresAt"]?.message
                  }
                  label={langStore.DesignShowcaseExpiresAtLabel}
                  nullText={langStore.optionalFieldHint}
                  onChange={(value: Date | null | null) =>
                    onExpiresAtChange(value)
                  }
                />
                <InputDateTime
                  value={registeredAt}
                  error={Boolean(registeredAtError)}
                  warning={
                    !registeredAtError &&
                    Boolean(designShowcaseUpdateStore.warnings["registeredAt"])
                  }
                  helperText={
                    registeredAtError ||
                    designShowcaseUpdateStore.warnings["registeredAt"]?.message
                  }
                  label={langStore.DesignShowcaseRegisteredAtLabel}
                  nullText={langStore.optionalFieldHint}
                  description={langStore.DesignShowcaseRegisteredAtDescription}
                  onChange={(value: Date | null | null) =>
                    onRegisteredAtChange(value)
                  }
                />
              </FormControl>
            </GroupContainer>

            <GroupContainer
              id="flags"
              errorText={designShowcaseUpdateStore.getCardErrors("flags")}
              title={langStore.groupDesignShowcaseFlagsLabel}
              collapsible={true}
              expanded={designShowcaseUpdateStore.getCardState("flags")}
              toggleExpand={() =>
                designShowcaseUpdateStore.toggleCardState("flags")
              }
            >
              <FormControl fullWidth>
                <InputBoolean
                  value={active}
                  error={Boolean(activeError)}
                  helperText={
                    activeError ||
                    designShowcaseUpdateStore.warnings["active"]?.message
                  }
                  label={langStore.DesignShowcaseActiveLabel}
                  onChange={(value: boolean) => onActiveChange(value)}
                />
                <InputBoolean
                  value={highlighted}
                  error={Boolean(highlightedError)}
                  helperText={
                    highlightedError ||
                    designShowcaseUpdateStore.warnings["highlighted"]?.message
                  }
                  label={langStore.DesignShowcaseHighlightedLabel}
                  onChange={(value: boolean) => onHighlightedChange(value)}
                />
              </FormControl>
            </GroupContainer>

            <GroupContainer
              id="contact"
              errorText={designShowcaseUpdateStore.getCardErrors("contact")}
              title={langStore.groupDesignShowcaseContactLabel}
              collapsible={true}
              expanded={designShowcaseUpdateStore.getCardState("contact")}
              toggleExpand={() =>
                designShowcaseUpdateStore.toggleCardState("contact")
              }
            >
              <FormControl fullWidth>
                <InputPhone
                  value={phone}
                  error={Boolean(phoneError)}
                  helperText={
                    phoneError ||
                    designShowcaseUpdateStore.warnings["phone"]?.message
                  }
                  label={langStore.DesignShowcasePhoneLabel}
                  onChange={(value: string) => onPhoneChange(value)}
                  nullText={langStore.optionalFieldHint}
                />
                <InputStringMask
                  value={taxId}
                  error={Boolean(taxIdError)}
                  warning={
                    !taxIdError &&
                    Boolean(designShowcaseUpdateStore.warnings["taxId"])
                  }
                  helperText={
                    taxIdError ||
                    designShowcaseUpdateStore.warnings["taxId"]?.message
                  }
                  label={langStore.DesignShowcaseTaxIdLabel}
                  onChange={(value: string) => onTaxIdChange(value)}
                  maskConfig={{
                    mask: "99-99999999-9",
                    masks: ["99-99999999-9"],
                  }}
                  nullText={langStore.optionalFieldHint}
                />
              </FormControl>
            </GroupContainer>

            <GroupContainer
              id="documents"
              errorText={designShowcaseUpdateStore.getCardErrors("documents")}
              title={langStore.groupDesignShowcaseDocumentsLabel}
              collapsible={true}
              expanded={designShowcaseUpdateStore.getCardState("documents")}
              toggleExpand={() =>
                designShowcaseUpdateStore.toggleCardState("documents")
              }
            >
              <FormControl fullWidth>
                <LoadFilesCRUDCompact
                  rows={attachmentFiles}
                  maxRows={1}
                  acceptFileTypes=".pdf"
                  menuActions={getViewAction(
                    (props) =>
                      designShowcaseUpdateStore.fileDownload(
                        props,
                        "attachment"
                      ),
                    (props) =>
                      designShowcaseUpdateStore.viewFilePdf(props, "attachment")
                  )}
                  showAddCommand={true}
                  showDeleteCommand={true}
                  label={langStore.DesignShowcaseAttachmentLabel}
                  onLoadFile={(file: File) => attachmentOnLoadFile(file)}
                  columns={[
                    { name: "mimetype", title: langStore.fileTypeLabel },
                    { name: "name", title: langStore.fileNameLabel },
                    { name: "formattedSize", title: langStore.fileSizeLabel },
                  ]}
                  onRestore={(id: string) => attachmentOnRestoreFile(id)}
                  onCommitChanges={(changes: GridChangeSet) =>
                    attachmentOnCommitFile(changes)
                  }
                  dummyCRUDRestoredObservable={dummyCRUDRestoredObservable}
                  error={attachmentError}
                />
                <LoadFilesCRUDCompact
                  rows={anyFileFiles}
                  maxRows={1}
                  menuActions={getViewAction(
                    (props) =>
                      designShowcaseUpdateStore.fileDownload(props, "anyFile"),
                    (props) =>
                      designShowcaseUpdateStore.viewFilePdf(props, "anyFile")
                  )}
                  showAddCommand={true}
                  showDeleteCommand={true}
                  label={langStore.DesignShowcaseAnyFileLabel}
                  onLoadFile={(file: File) => anyFileOnLoadFile(file)}
                  columns={[
                    { name: "mimetype", title: langStore.fileTypeLabel },
                    { name: "name", title: langStore.fileNameLabel },
                    { name: "formattedSize", title: langStore.fileSizeLabel },
                  ]}
                  onRestore={(id: string) => anyFileOnRestoreFile(id)}
                  onCommitChanges={(changes: GridChangeSet) =>
                    anyFileOnCommitFile(changes)
                  }
                  dummyCRUDRestoredObservable={dummyCRUDRestoredObservable}
                  error={anyFileError}
                />
                <AvatarField
                  avatarData={avatarData}
                  entityName="DesignShowcase"
                  fieldName="avatar"
                  entityId={avatarEntityId}
                  previewUrl={avatarPreviewUrl}
                  label={langStore.DesignShowcaseAvatarLabel}
                  onAvatarChange={(blob: Blob) => onAvatarChange(blob)}
                  onAvatarDelete={() => onAvatarDelete()}
                />
              </FormControl>
            </GroupContainer>

            <GroupContainer
              id="richContent"
              errorText={designShowcaseUpdateStore.getCardErrors("richContent")}
              title={langStore.groupDesignShowcaseRichContentLabel}
              collapsible={true}
              expanded={designShowcaseUpdateStore.getCardState("richContent")}
              toggleExpand={() =>
                designShowcaseUpdateStore.toggleCardState("richContent")
              }
            >
              <FormControl fullWidth>
                <InputStringList
                  value={keywords}
                  error={Boolean(keywordsError)}
                  helperText={
                    keywordsError ||
                    designShowcaseUpdateStore.warnings["keywords"]?.message
                  }
                  label={langStore.DesignShowcaseKeywordsLabel}
                  description={langStore.DesignShowcaseKeywordsDescription}
                  onChange={(value: string[]) => onKeywordsChange(value)}
                />
                <InputHtmlString
                  value={body}
                  error={Boolean(bodyError)}
                  helperText={
                    bodyError ||
                    designShowcaseUpdateStore.warnings["body"]?.message
                  }
                  label={langStore.DesignShowcaseBodyLabel}
                  onChange={(value: string) => onBodyChange(value)}
                  fragment={true}
                  description={langStore.DesignShowcaseBodyDescription}
                />
              </FormControl>
            </GroupContainer>

            <GroupContainer
              id="advanced"
              errorText={designShowcaseUpdateStore.getCardErrors("advanced")}
              title={langStore.groupDesignShowcaseAdvancedLabel}
              collapsible={true}
              expanded={designShowcaseUpdateStore.getCardState("advanced")}
              toggleExpand={() =>
                designShowcaseUpdateStore.toggleCardState("advanced")
              }
            >
              <FormControl fullWidth>
                <InputCodeEditor
                  value={metadata}
                  error={Boolean(metadataError)}
                  helperText={
                    metadataError ||
                    designShowcaseUpdateStore.warnings["metadata"]?.message
                  }
                  label={langStore.DesignShowcaseMetadataLabel}
                  onChange={(value: string) => onMetadataChange(value)}
                  language="json"
                />
                {internalNoteIsVisible && (
                  <InputString
                    value={internalNote}
                    error={Boolean(internalNoteError)}
                    warning={
                      !internalNoteError &&
                      Boolean(
                        designShowcaseUpdateStore.warnings["internalNote"]
                      )
                    }
                    helperText={
                      internalNoteError ||
                      designShowcaseUpdateStore.warnings["internalNote"]
                        ?.message
                    }
                    label={langStore.DesignShowcaseInternalNoteLabel}
                    onChange={(value: string) => onInternalNoteChange(value)}
                    nullText={langStore.optionalFieldHint}
                    description={
                      langStore.DesignShowcaseInternalNoteDescription
                    }
                  />
                )}
                <InputSecret
                  value={apiKey}
                  error={Boolean(apiKeyError)}
                  helperText={
                    apiKeyError ||
                    designShowcaseUpdateStore.warnings["apiKey"]?.message
                  }
                  label={langStore.DesignShowcaseApiKeyLabel}
                  onChange={(value: string) => onApiKeyChange(value)}
                  description={langStore.DesignShowcaseApiKeyDescription}
                />
              </FormControl>
            </GroupContainer>
          </Box>
          {(showSaveFooter ?? !showToolbox) && (
            <FormSaveFooter
              onSave={() => onSave()}
              label={langStore.saveEntity}
              disabled={saving}
            />
          )}
          <PDFViewerDialog
            open={designShowcaseUpdateStore.pdfViewerOpen}
            onClose={() => designShowcaseUpdateStore.closePdfViewer()}
            pdfUrl={designShowcaseUpdateStore.pdfViewerUrl}
            title={designShowcaseUpdateStore.pdfViewerTitle}
          />
        </Grid>
      );
    }
  );
